import React from "react";
import { useTheme, styled } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import { Grid, CircularProgress, Box, Collapse, Tooltip } from "@mui/material";
import ConfirmModal from "../Common/CommonModal";
import { removeAuthObjects, toastError, toastSuccess, hasLabelRequestAccess, getTokenPayload } from "../../utils";
import appLogo from "../../assets/img/Fronic_Logo_white.png";
import { Link, useNavigate } from "react-router-dom";
import * as commonAction from "../Common/actions";
import FullScreenLoader from "../Common/FullScreenLoader";
import CustomAddModal from "../Common/CustomAddModal";
import AdminActivityPanel from "../AdminActivityPanel";
import LeaveApprovalModal from "./LeaveApprovalModal";
import * as profileActions from "../Profile/actions";
import {
  LayoutDashboard, Disc3, Ban, Video, History, Scale, Link2, ListMusic,
  UserPlus, UserCog, Users, FileText, Ticket, ListChecks, Megaphone,
  Wallet, Music, LogOut, Menu, ChevronLeft, ChevronRight, ChevronDown,
  ClipboardList, Bell, BarChart3, Link as LinkIcon, User, CalendarCheck, ArrowRightLeft, Zap
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const SUPER_ADMIN_EMAILS = [
  'harjaspreet.singh@ambalaproductions.in',
  'vijay.kumar@ambalaproductions.in',
];
const EXCLUDED_EMAILS = [
  'gulshan@ambalaproductions.in',
];

function renderBoldMessage(message) {
  const parts = message.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <span key={i} style={{ fontWeight: 600, color: '#F1F5F9' }}>{part.slice(2, -2)}</span>
      : part
  );
}

const drawerWidth = 270;
const ICON_SIZE = 20;
const ICON_STROKE = 1.5;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  background: '#1A1D27',
  borderBottom: '1px solid #2E3247',
  boxShadow: 'none',
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: '300ms',
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: '300ms',
    }),
  }),
}));

const DrawerStyled = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: '300ms',
    }),
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      background: 'linear-gradient(180deg, #141620 0%, #12141E 100%)',
      borderRight: 'none',
      boxShadow: 'inset -8px 0 12px -6px rgba(0, 0, 0, 0.3)',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: '300ms',
      }),
      overflowX: 'hidden',
    },
  }),
  ...(!open && {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: '300ms',
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(${theme.spacing(9)} + 1px)`,
    },
    '& .MuiDrawer-paper': {
      background: 'linear-gradient(180deg, #141620 0%, #12141E 100%)',
      borderRight: 'none',
      boxShadow: 'inset -8px 0 12px -6px rgba(0, 0, 0, 0.3)',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: '300ms',
      }),
      overflowX: 'hidden',
      width: `calc(${theme.spacing(7)} + 1px)`,
      [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
      },
    },
  }),
}));

// Section definitions for grouping nav items
const navSections = [
  { label: 'Main', keys: ['Task', 'Stats'] },
  { label: 'Releases', keys: ['Review Releases', 'Approved Releases', 'Rejected Releases', 'Takendown Releases'], collapsible: true, icon: <Disc3 size={ICON_SIZE} strokeWidth={ICON_STROKE} /> },
  { label: 'Media', keys: ['Video Releases', 'Video History'] },
  { label: 'Distribution', keys: ['UGC Claims', 'Profile Linking', 'Playlist Pitching', 'Lyrics Distribution'] },
  { label: 'Management', keys: ['New Label Requests', 'Admin Actions', 'User Details', 'Report Requests', 'Tickets'] },
  { label: 'Operations', keys: ['Musilinks', 'Whitelisting', 'Promotions', 'Instant Promotions', 'Requested Payouts', 'Analytics Upload', 'Content Transfer'] },
];

const initialListItems = [
  { label: "Task", icon: <ClipboardList size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/", countKey: "open_tracker" },
  { label: "Stats", icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/stats" },
  { label: "Review Releases", icon: <Disc3 size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/releases/review", countKey: "in_review" },
  { label: "Approved Releases", icon: <Disc3 size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/release/approved" },
  { label: "Rejected Releases", icon: <Disc3 size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/release/rejected" },
  { label: "Takendown Releases", icon: <Ban size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/release/takendown" },
  { label: "Video Releases", icon: <Video size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/video_releases/review", countKey: "video_releases" },
  { label: "Video History", icon: <History size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/video_releases/history" },
  { label: "UGC Claims", icon: <Scale size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/ugc_claims", countKey: "ugc_claim" },
  { label: "Profile Linking", icon: <Link2 size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/profile_linking", countKey: "profile_linking" },
  { label: "Playlist Pitching", icon: <ListMusic size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/playlist_pitching", countKey: "pitch_playlist" },
  { label: "New Label Requests", icon: <UserPlus size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/label-requests", countKey: "label_requests" },
  { label: "Admin Actions", icon: <UserCog size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/user_actions" },
  { label: "User Details", icon: <Users size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/user_details" },
  { label: "Report Requests", icon: <FileText size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/report_requests", countKey: "reports_requested" },
  { label: "Tickets", icon: <Ticket size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/tickets", countKey: "open_tickets" },
  { label: "Whitelisting", icon: <ListChecks size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/whitelisting", countKey: "whitelisting" },
  { label: "Promotions", icon: <Megaphone size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/promotions" },
  { label: "Instant Promotions", icon: <Zap size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/instant-promotions" },
  { label: "Requested Payouts", icon: <Wallet size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/payout", countKey: "withdrawl_requested" },
  { label: "Lyrics Distribution", icon: <Music size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/lyrics_distribution", countKey: "distribute_lyrics" },
  { label: "Analytics Upload", icon: <BarChart3 size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/analytics-upload" },
  { label: "Content Transfer", icon: <ArrowRightLeft size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/content-transfer" },
  { label: "Musilinks", icon: <LinkIcon size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/musilinks" },
  { label: "Logout", icon: <LogOut size={ICON_SIZE} strokeWidth={ICON_STROKE} />, path: "/login" },
];

// Build grouped items from listItems (excluding Logout)
function buildGroupedItems(listItems) {
  const itemMap = {};
  listItems.forEach(item => { itemMap[item.label] = item; });
  const groups = [];
  navSections.forEach(section => {
    const items = section.keys
      .map(key => itemMap[key])
      .filter(Boolean);
    if (items.length > 0) {
      groups.push({
        label: section.label,
        items,
        collapsible: section.collapsible || false,
        icon: section.icon || null,
      });
    }
  });
  return groups;
}

const SectionHeader = ({ label, open }) => {
  if (!open) return <Box sx={{ my: 0.5 }} />;
  return (
    <Typography
      sx={{
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#4A5068',
        px: 2,
        pt: 1.5,
        pb: 0.5,
        userSelect: 'none',
      }}
    >
      {label}
    </Typography>
  );
};

export default function MainContainer(props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(true);
  const [loader, setLoader] = React.useState(false);
  const [confirmOpen, setConfimOpen] = React.useState(false);
  const [listItems, setListItems] = React.useState([...initialListItems]);
  const [counts, setCounts] = React.useState({});
  const [awayStatus, setAwayStatus] = React.useState(false);
  const [awayText, setAwayText] = React.useState("");
  const [openAwayText, setOpenAwayText] = React.useState(false);
  const [sidenavReady, setSidenavReady] = React.useState(false);
  const [hasNewNotification, setHasNewNotification] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = React.useState(false);
  const [loadingNotifs, setLoadingNotifs] = React.useState(false);
  const notifRef = React.useRef(null);
  const [showLeaveModal, setShowLeaveModal] = React.useState(false);
  const [pendingLeaveCount, setPendingLeaveCount] = React.useState(0);
  const [expandedSections, setExpandedSections] = React.useState(() => {
    // Auto-expand sections that contain the current active route
    const path = window.location.pathname;
    const initial = {};
    navSections.forEach(section => {
      if (section.collapsible) {
        const hasActive = initialListItems.some(
          item => section.keys.includes(item.label) && item.path === path
        );
        // Also expand for detail pages (Releases section)
        const hasDetailActive = (
          path.startsWith('/view_releases/') || path.startsWith('/view_video_release/')
        ) && section.keys.some(key => {
          const navItem = initialListItems.find(i => i.label === key);
          return navItem && (
            navItem.path === '/releases/review' || navItem.path === '/release/approved' ||
            navItem.path === '/release/rejected' || navItem.path === '/release/takendown'
          );
        });
        if (hasActive || hasDetailActive) initial[section.label] = true;
      }
    });
    return initial;
  });

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSidenavReady(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-collapse Releases section after 10s if not on a release page
  React.useEffect(() => {
    const path = props.location?.pathname || '';
    const releasePaths = ['/releases/review', '/release/approved', '/release/rejected', '/release/takendown'];
    const isOnReleasePage = releasePaths.includes(path) || path.startsWith('/view_releases/');
    if (!isOnReleasePage && expandedSections['Releases']) {
      const collapseTimer = setTimeout(() => {
        setExpandedSections(prev => ({ ...prev, Releases: false }));
      }, 10000);
      return () => clearTimeout(collapseTimer);
    }
  }, [props.location?.pathname, expandedSections]);

  React.useEffect(() => {
    const canAccessLabelRequests = hasLabelRequestAccess();
    commonAction
      .countMeterAdmin()
      .then((res) => {
        const body = res.body || {};
        setCounts(body);
        let updatedList = initialListItems.filter(item => {
          if (item.label === "New Label Requests" && !canAccessLabelRequests) return false;
          if (item.label === "Analytics Upload" && (!canAccessLabelRequests || getTokenPayload()?.email === 'gulshan@ambalaproductions.in')) return false;
          if (item.label === "Content Transfer" && !canAccessLabelRequests) return false;
          if (item.label === "Instant Promotions" && getTokenPayload()?.email !== 'harjaspreet.singh@ambalaproductions.in') return false;
          return true;
        });
        setListItems(updatedList);
      })
      .catch(() => {});
    commonAction.checkAwayStatus().then((res) => {
      setAwayStatus(
        res && res.body && res.body.status && res.body.status === "yes"
          ? true
          : false || false
      );
      setAwayText((res && res.body && res.body.text) || "");
    });
  }, []);

  // Activity-aware admin heartbeat + auto-logout on 1 hour inactivity
  React.useEffect(() => {
    const INTERVAL = 300000; // 5 minutes
    const IDLE_TIMEOUT = 3600000; // 1 hour
    const IDLE_CHECK_INTERVAL = 60000; // 1 minute
    const lastActivityTimeRef = { current: Date.now() };
    let lastHeartbeatSent = Date.now();

    // Send initial heartbeat
    commonAction.adminHeartbeat().catch(console.error);

    const handleActivity = () => {
      lastActivityTimeRef.current = Date.now();
      // If idle for over 5 min, send heartbeat immediately on activity resume
      if (Date.now() - lastHeartbeatSent >= INTERVAL) {
        commonAction.adminHeartbeat().catch(console.error);
        lastHeartbeatSent = Date.now();
      }
    };

    document.addEventListener("click", handleActivity);
    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("keydown", handleActivity);

    const heartbeatInterval = setInterval(() => {
      if (Date.now() - lastActivityTimeRef.current < INTERVAL) {
        commonAction.adminHeartbeat().catch(console.error);
        lastHeartbeatSent = Date.now();
      }
    }, INTERVAL);

    const idleCheckInterval = setInterval(() => {
      if (Date.now() - lastActivityTimeRef.current >= IDLE_TIMEOUT) {
        removeAuthObjects();
        navigate("/login");
      }
    }, IDLE_CHECK_INTERVAL);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(idleCheckInterval);
      document.removeEventListener("click", handleActivity);
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("keydown", handleActivity);
    };
  }, []);

  // Poll notification bell status every 30s
  React.useEffect(() => {
    const checkBell = () => {
      commonAction.checkAdminNotificationBell()
        .then((res) => {
          if (res && res.body) setHasNewNotification(res.body.hasNew === true);
        })
        .catch(() => {});
    };
    checkBell();
    const interval = setInterval(checkBell, 30000);
    return () => clearInterval(interval);
  }, []);

  // Poll pending leave request count for super admins
  const tokenEmail = getTokenPayload()?.email;
  const isSuperAdmin = tokenEmail
    && SUPER_ADMIN_EMAILS.includes(tokenEmail)
    && !EXCLUDED_EMAILS.includes(tokenEmail);

  React.useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchCount = () => {
      profileActions.getPendingLeaveRequests()
        .then((res) => {
          setPendingLeaveCount(Array.isArray(res.body) ? res.body.length : 0);
        })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 1800000);
    return () => clearInterval(interval);
  }, [isSuperAdmin]);

  // Click outside to close notification dropdown
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    window.addEventListener("resize", (event) => {
      if (document.body.clientWidth < 700) {
        if (open) setOpen(false);
      } else {
        if (!open) setOpen(true);
      }
    });
    return () => {
      window.removeEventListener("resize", () => {});
    };
  }, [open]);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const header = () => {
    const path = props.location?.pathname || props.match?.path || '';
    switch (path) {
      case "/": return "Task";
      case "/stats": return "Stats";
      case "/news": return "News";
      case "/releases/review": return "Review Releases";
      case "/release/approved": return "Approved Releases";
      case "/release/rejected": return "Rejected Releases";
      case "/release/takendown": return "Taken Down Releases";
      case "/video_releases/review": return "Review Video Releases";
      case "/video_releases/history": return "Video Releases History";
      case "/user_details": return "User Details";
      case "/user_actions": return "Admin Actions";
      case "/report_requests": return "Report Requests";
      case "/tickets": return "Tickets";
      case "/payout": return "Requested Payouts";
      case "/notifications": return "Notifications";
      case "/ugc_claims": return "UGC Claims";
      case "/profile_linking": return "Profile Linking";
      case "/playlist_pitching": return "Playlist Pitching";
      case "/lyrics_distribution": return "Lyrics Distribution";
      case "/label-requests": return "New Label Requests";
      case "/whitelisting": return "Whitelisting";
      case "/promotions": return "Promotions";
      case "/instant-promotions": return "Instant Promotions";
      case "/analytics-upload": return "Analytics Upload";
      case "/musilinks": return "Musilinks";
      case "/content-transfer": return "Content Transfer";
      case "/profile": return "My Profile";
      default: return "";
    }
  };

  const handleConfirmClickOpen = () => setConfimOpen(true);
  const handleConfirmClose = () => setConfimOpen(false);

  const onConfirmLogout = () => {
    onSetLoader(true);
    removeAuthObjects();
    setTimeout(() => {
      onSetLoader(false);
      return navigate("/login");
    }, 1000);
  };

  const onChangeAwayStatus = (checked = false) => {
    onSetLoader(true);
    commonAction
      .changeAwayStatusAdmin({
        status: checked ? "yes" : "no",
        text: awayText || "",
      })
      .then((res) => {
        onSetLoader(false);
        setAwayStatus(checked);
        setOpenAwayText(false);
        toastSuccess(res.message || "Status changed successfully");
      })
      .catch((err) => {
        onSetLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onSetLoader = (action) => setLoader(action);

  const onSetAwayText = () => {
    if (!awayStatus) {
      setOpenAwayText(true);
    } else {
      onChangeAwayStatus(false);
    }
  };

  const handleBellClick = () => {
    const opening = !showNotifDropdown;
    setShowNotifDropdown(opening);
    if (opening) {
      setLoadingNotifs(true);
      commonAction.getAdminNotifications()
        .then((res) => {
          setNotifications(res?.body || []);
          setLoadingNotifs(false);
        })
        .catch(() => setLoadingNotifs(false));
    }
    if (hasNewNotification) {
      commonAction.markAdminNotificationsRead().catch(() => {});
      setHasNewNotification(false);
    }
  };

  const currentPath = props.location?.pathname || '';
  const fromPath = props.location?.state?.from || '';

  const detailPageTitles = {
    '/view_releases/': 'Release Details',
    '/view_video_release/': 'Video Release Details',
    '/view_tickets/': 'Ticket Details',
  };

  const getBreadcrumbs = () => {
    const crumbs = [{ label: 'Task', path: '/' }];
    const pageTitle = header();

    // Check if it's a detail page
    const detailMatch = Object.entries(detailPageTitles).find(([prefix]) => currentPath.startsWith(prefix));

    if (detailMatch) {
      // Detail page: add parent if fromPath exists
      if (fromPath) {
        const parentTitle = (() => {
          const parentItem = initialListItems.find(i => i.path === fromPath);
          return parentItem ? parentItem.label : '';
        })();
        if (parentTitle) {
          crumbs.push({ label: parentTitle, path: fromPath });
        }
      }
      crumbs.push({ label: detailMatch[1], path: null });
    } else if (currentPath !== '/') {
      crumbs.push({ label: pageTitle, path: null });
    }

    return crumbs;
  };
  const isActive = (path) => {
    if (currentPath === path) return true;
    // On detail pages, highlight the parent nav item
    if (currentPath.startsWith('/view_tickets/') && path === '/tickets') return true;
    if ((currentPath.startsWith('/view_releases/') || currentPath.startsWith('/view_video_release/')) && fromPath) {
      return path === fromPath;
    }
    return false;
  };

  const logoutItem = listItems.find(item => item.label === "Logout");
  const groupedItems = React.useMemo(
    () => buildGroupedItems(listItems.filter(item => item.label !== "Logout")),
    [listItems]
  );

  const activeScrollRef = React.useCallback((node) => {
    if (node) {
      setTimeout(() => {
        node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const renderNavItem = (item) => {
    const active = isActive(item.path);
    const count = item.countKey ? counts[item.countKey] : null;

    const linkContent = (
      <Link
        key={item.label}
        to={item.path}
        style={{ color: 'inherit', textDecoration: 'none' }}
        ref={active ? activeScrollRef : undefined}
      >
        <ListItemButton
          sx={{
            borderRadius: '10px',
            mb: 0.3,
            py: 1.1,
            px: open ? 1.5 : 0,
            justifyContent: open ? 'flex-start' : 'center',
            backgroundColor: active ? 'rgba(108, 99, 255, 0.14)' : 'transparent',
            color: active ? '#F1F5F9' : '#8B92A5',
            fontWeight: active ? 600 : 400,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: active ? 'rgba(108, 99, 255, 0.18)' : 'rgba(108, 99, 255, 0.07)',
              transform: open ? 'translateX(2px)' : 'none',
            },
          }}
        >
          <ListItemIcon sx={{
            minWidth: open ? 34 : 'unset',
            mr: open ? undefined : 0,
            color: active ? '#6C63FF' : '#8B92A5',
            transition: 'all 0.2s ease',
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: active ? 'rgba(108, 99, 255, 0.18)' : 'transparent',
              transform: active ? 'scale(1.08)' : 'scale(1)',
              transition: 'all 0.2s ease',
            }}>
              {item.icon}
            </Box>
          </ListItemIcon>
          {open && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.82rem',
                fontWeight: active ? 600 : 400,
                letterSpacing: '0.01em',
              }}
            />
          )}
          {open && count > 0 && (
            <Chip
              label={count}
              size="small"
              sx={{
                height: 18,
                minWidth: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                borderRadius: '9px',
                backgroundColor: 'rgba(108, 99, 255, 0.2)',
                color: '#8B8AFF',
                boxShadow: '0 0 8px rgba(108, 99, 255, 0.25)',
                animation: 'badgePulse 2.5s ease-in-out infinite',
                '& .MuiChip-label': {
                  px: 0.6,
                },
              }}
            />
          )}
        </ListItemButton>
      </Link>
    );

    if (!open) {
      return (
        <Tooltip
          key={item.label}
          title={item.label}
          placement="right"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#1A1D27',
                color: '#F1F5F9',
                fontSize: '0.75rem',
                fontWeight: 500,
                border: '1px solid #2E3247',
                borderRadius: '8px',
                px: 1.5,
                py: 0.5,
              },
            },
            arrow: {
              sx: {
                color: '#1A1D27',
                '&::before': { border: '1px solid #2E3247' },
              },
            },
          }}
        >
          {linkContent}
        </Tooltip>
      );
    }

    return linkContent;
  };

  return (
    <>
      <FullScreenLoader open={loader} />
      <div style={{ display: 'flex' }}>
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: '36px',
                color: '#F1F5F9',
                ...(open && { display: 'none' }),
              }}
            >
              <Menu size={22} />
            </IconButton>
            <Box sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getBreadcrumbs().map((crumb, i, arr) => (
                  <React.Fragment key={i}>
                    {i > 0 && <ChevronRight size={14} color="#4A5068" style={{ flexShrink: 0 }} />}
                    {crumb.path && i < arr.length - 1 ? (
                      <Typography
                        noWrap
                        onClick={() => navigate(crumb.path)}
                        sx={{
                          color: '#64748B',
                          fontSize: '1rem',
                          fontWeight: 400,
                          cursor: 'pointer',
                          transition: 'color 0.2s ease',
                          '&:hover': { color: '#94A3B8' },
                        }}
                      >
                        {crumb.label}
                      </Typography>
                    ) : (
                      <Typography noWrap sx={{
                        color: '#F1F5F9',
                        fontSize: '1rem',
                        fontWeight: 600,
                        letterSpacing: '0.01em',
                      }}>
                        {crumb.label}
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <span style={{ fontSize: '1.15rem', lineHeight: 1 }}>
                  {(() => {
                    const hour = new Date().getHours();
                    if (hour >= 5 && hour < 12) return '☀️';
                    if (hour >= 12 && hour < 17) return '🌤️';
                    if (hour >= 17 && hour < 21) return '🌅';
                    return '🦉';
                  })()}
                </span>
                <Typography sx={{
                  color: '#94A3B8',
                  fontSize: '0.9rem',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                }}>
                  {(() => {
                    const hour = new Date().getHours();
                    if (hour >= 5 && hour < 12) return 'Good Morning';
                    if (hour >= 12 && hour < 17) return 'Good Afternoon';
                    if (hour >= 17 && hour < 21) return 'Good Evening';
                    return 'Heyy Night Owl';
                  })()}
                  {(() => {
                    const name = getTokenPayload()?.name;
                    return name ? `, ${name}` : '';
                  })()}
                </Typography>
                {!EXCLUDED_EMAILS.includes(tokenEmail) && (
                <Box
                    onClick={() => navigate('/profile')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.6,
                      color: currentPath === '/profile' ? '#6C63FF' : '#94A3B8',
                      backgroundColor: currentPath === '/profile' ? 'rgba(108, 99, 255, 0.14)' : 'transparent',
                      border: '1.5px solid #6C63FF',
                      borderRadius: '20px',
                      px: 1.2,
                      py: 0.5,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(108, 99, 255, 0.12)',
                        color: '#6C63FF',
                        borderColor: '#6C63FF',
                      },
                    }}
                  >
                    <User size={16} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      My Profile
                    </Typography>
                  </Box>
                )}
                {isSuperAdmin && (
                  <Tooltip title="Leave Requests" placement="bottom" arrow slotProps={{
                    tooltip: { sx: { backgroundColor: '#1A1D27', color: '#F1F5F9', fontSize: '0.75rem', fontWeight: 500, border: '1px solid #2E3247', borderRadius: '8px', px: 1.5, py: 0.5 } },
                    arrow: { sx: { color: '#1A1D27', '&::before': { border: '1px solid #2E3247' } } },
                  }}>
                    <IconButton
                      onClick={() => setShowLeaveModal(true)}
                      sx={{
                        color: '#94A3B8',
                        position: 'relative',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(108, 99, 255, 0.12)',
                          color: '#6C63FF',
                        },
                      }}
                    >
                      <CalendarCheck size={20} />
                      {pendingLeaveCount > 0 && (
                        <Box sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          minWidth: 16,
                          height: 16,
                          borderRadius: '8px',
                          backgroundColor: '#EF4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.4,
                        }}>
                          <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700, lineHeight: 1 }}>
                            {pendingLeaveCount > 9 ? '9+' : pendingLeaveCount}
                          </Typography>
                        </Box>
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Box ref={notifRef} sx={{ position: 'relative' }}>
                  <IconButton onClick={handleBellClick} sx={{ color: '#94A3B8', position: 'relative' }}>
                    <Bell size={24} />
                    {hasNewNotification && (
                      <Box sx={{
                        position: 'absolute', top: 6, right: 6,
                        width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: '#EF4444',
                      }} />
                    )}
                  </IconButton>
                  {showNotifDropdown && (
                    <Box sx={{
                      position: 'absolute', top: '100%', right: 0, mt: 0.5,
                      width: 420, maxHeight: 400, overflowY: 'auto',
                      backgroundColor: '#1A1D27', border: '1px solid #2E3247',
                      borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      zIndex: 9999,
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-thumb': { background: '#2E3247', borderRadius: '4px' },
                    }}>
                      <Typography sx={{ px: 2, py: 1.5, fontSize: '0.85rem', fontWeight: 600, color: '#F1F5F9', borderBottom: '1px solid #2E3247' }}>
                        Notifications
                      </Typography>
                      {loadingNotifs ? (
                        <Box display="flex" justifyContent="center" py={3}>
                          <CircularProgress size={22} sx={{ color: '#6C63FF' }} />
                        </Box>
                      ) : notifications.length === 0 ? (
                        <Typography sx={{ px: 2, py: 3, fontSize: '0.8rem', color: '#64748B', textAlign: 'center' }}>
                          No notifications yet
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1 }}>
                          {notifications.map((n) => (
                            <Box
                              key={n.notification_id}
                              onClick={() => {
                                setShowNotifDropdown(false);
                                if (n.type === 'leave_approved' || n.type === 'leave_rejected') {
                                  navigate('/profile?tab=leaves');
                                } else if (n.tracker_id) {
                                  navigate(`/?task=${n.tracker_id}`);
                                }
                              }}
                              sx={{
                                px: 1.5, py: 1.2,
                                cursor: 'pointer',
                                borderRadius: '8px',
                                backgroundColor: '#1E2130',
                                transition: 'background 0.15s ease',
                                '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.10)' },
                              }}
                            >
                              <Typography sx={{
                                fontSize: '0.8rem',
                                color: n.type === 'leave_approved' ? '#4ADE80' : n.type === 'leave_rejected' ? '#F87171' : '#CBD5E1',
                                lineHeight: 1.5,
                              }}>
                                {renderBoldMessage(n.message)}
                              </Typography>
                              <Typography sx={{ fontSize: '0.65rem', color: '#64748B', textAlign: 'right', mt: 0.5 }}>
                                {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <DrawerStyled variant="permanent" open={open}>
          {/* Logo area */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'flex-end' : 'center',
            px: 1,
            py: 1.5,
            minHeight: '64px',
          }}>
            {open && (
              <Box sx={{
                width: '100%',
                height: '40px',
                display: 'flex',
              }}>
                <img
                  style={{
                    margin: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    cursor: 'pointer'
                  }}
                  src={appLogo}
                  alt=""
                  onClick={() => navigate("/")}
                />
              </Box>
            )}
            <IconButton
              onClick={open ? handleDrawerClose : handleDrawerOpen}
              sx={{ color: '#94A3B8' }}
            >
              {open ? (
                theme.direction === "rtl" ? <ChevronRight size={20} /> : <ChevronLeft size={20} />
              ) : (
                <Menu size={20} />
              )}
            </IconButton>
          </Box>
          {/* Divider glow */}
          <Box sx={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, #6C63FF40 50%, transparent 100%)',
          }} />

          {/* Nav content wrapper */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: '3px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: '#2E3247',
              borderRadius: '3px',
            },
          }}>
            <List sx={{ px: 1, pt: 0.5, flex: 1 }}>
              {!sidenavReady && (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={30} sx={{ color: '#6C63FF' }} />
                </Box>
              )}
              {sidenavReady && groupedItems.map((group) => {
                if (group.collapsible) {
                  const isExpanded = !!expandedSections[group.label];
                  const hasActiveChild = group.items.some(item => isActive(item.path));
                  const totalCount = group.items.reduce((sum, item) => {
                    const c = item.countKey ? (counts[item.countKey] || 0) : 0;
                    return sum + c;
                  }, 0);

                  const groupButton = (
                      <ListItemButton
                        onClick={() => {
                          if (open) {
                            setExpandedSections(prev => ({
                              ...prev,
                              [group.label]: !prev[group.label],
                            }));
                          } else {
                            handleDrawerOpen();
                            setExpandedSections(prev => ({ ...prev, [group.label]: true }));
                          }
                        }}
                        sx={{
                          borderRadius: '10px',
                          mb: 0.3,
                          py: 1.1,
                          px: open ? 1.5 : 0,
                          justifyContent: open ? 'flex-start' : 'center',
                          backgroundColor: hasActiveChild && !isExpanded ? 'rgba(108, 99, 255, 0.14)' : 'transparent',
                          color: hasActiveChild ? '#F1F5F9' : '#8B92A5',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: 'rgba(108, 99, 255, 0.07)',
                            transform: open ? 'translateX(2px)' : 'none',
                          },
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: open ? 34 : 'unset',
                          mr: open ? undefined : 0,
                          color: hasActiveChild ? '#6C63FF' : '#8B92A5',
                          transition: 'all 0.2s ease',
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            backgroundColor: hasActiveChild ? 'rgba(108, 99, 255, 0.18)' : 'transparent',
                            transform: hasActiveChild ? 'scale(1.08)' : 'scale(1)',
                            transition: 'all 0.2s ease',
                          }}>
                            {group.icon}
                          </Box>
                        </ListItemIcon>
                        {open && (
                          <ListItemText
                            primary={group.label}
                            primaryTypographyProps={{
                              fontSize: '0.82rem',
                              fontWeight: hasActiveChild ? 600 : 400,
                              letterSpacing: '0.01em',
                            }}
                          />
                        )}
                        {open && totalCount > 0 && (
                          <Chip
                            label={totalCount}
                            size="small"
                            sx={{
                              height: 18,
                              minWidth: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              borderRadius: '9px',
                              backgroundColor: 'rgba(108, 99, 255, 0.2)',
                              color: '#8B8AFF',
                              boxShadow: '0 0 8px rgba(108, 99, 255, 0.25)',
                              animation: totalCount > 0 ? 'badgePulse 2.5s ease-in-out infinite' : 'none',
                              mr: 0.5,
                              '& .MuiChip-label': { px: 0.6 },
                            }}
                          />
                        )}
                        {open && (
                          <ChevronDown
                            size={16}
                            style={{
                              transition: 'transform 0.25s ease',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              opacity: 0.5,
                            }}
                          />
                        )}
                      </ListItemButton>
                  );

                  return (
                    <React.Fragment key={group.label}>
                      {open && <Box sx={{ my: 0.3 }} />}
                      {!open ? (
                        <Tooltip
                          title={group.label}
                          placement="right"
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#1A1D27',
                                color: '#F1F5F9',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                border: '1px solid #2E3247',
                                borderRadius: '8px',
                                px: 1.5,
                                py: 0.5,
                              },
                            },
                            arrow: {
                              sx: {
                                color: '#1A1D27',
                                '&::before': { border: '1px solid #2E3247' },
                              },
                            },
                          }}
                        >
                          {groupButton}
                        </Tooltip>
                      ) : groupButton}
                      <Collapse in={isExpanded && open} timeout={250}>
                        <Box sx={{ pl: 1.5 }}>
                          {group.items.map(renderNavItem)}
                        </Box>
                      </Collapse>
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={group.label}>
                    <SectionHeader label={group.label} open={open} />
                    {group.items.map(renderNavItem)}
                  </React.Fragment>
                );
              })}
            </List>

            {/* Logout at bottom */}
            {sidenavReady && logoutItem && (
              <Box sx={{ mt: 'auto', px: 1, pb: 1.5 }}>
                {open && <Box sx={{
                  height: '1px',
                  mx: 1,
                  mb: 1,
                  background: 'linear-gradient(90deg, transparent 0%, #2E324780 50%, transparent 100%)',
                }} />}
                {(() => {
                  const logoutButton = (
                    <ListItemButton
                      onClick={handleConfirmClickOpen}
                      sx={{
                        borderRadius: '10px',
                        py: 1.1,
                        px: open ? 1.5 : 0,
                        justifyContent: open ? 'flex-start' : 'center',
                        color: '#CF6679',
                        backgroundColor: 'rgba(239, 68, 68, 0.06)',
                        border: '1px solid rgba(239, 68, 68, 0.12)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: 'rgba(239, 68, 68, 0.14)',
                          color: '#EF4444',
                          border: '1px solid rgba(239, 68, 68, 0.25)',
                          boxShadow: '0 0 12px rgba(239, 68, 68, 0.1)',
                          '& .MuiListItemIcon-root': {
                            color: '#EF4444',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: open ? 34 : 'unset',
                        mr: open ? undefined : 0,
                        color: '#CF6679',
                        transition: 'color 0.2s ease',
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        }}>
                          {logoutItem.icon}
                        </Box>
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          primary="Logout"
                          primaryTypographyProps={{
                            fontSize: '0.82rem',
                            fontWeight: 500,
                            letterSpacing: '0.01em',
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                  return !open ? (
                    <Tooltip
                      title="Logout"
                      placement="right"
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: '#1A1D27',
                            color: '#F1F5F9',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            border: '1px solid #2E3247',
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.5,
                          },
                        },
                        arrow: {
                          sx: {
                            color: '#1A1D27',
                            '&::before': { border: '1px solid #2E3247' },
                          },
                        },
                      }}
                    >
                      {logoutButton}
                    </Tooltip>
                  ) : logoutButton;
                })()}
              </Box>
            )}
          </Box>
        </DrawerStyled>
        <main style={{ flexGrow: 1, padding: '24px', backgroundColor: '#0F1117', minHeight: '100vh' }}>
          <style>{`
            @keyframes fadeInContent {
              from { opacity: 0; transform: translateY(6px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes badgePulse {
              0%, 100% { box-shadow: 0 0 8px rgba(108, 99, 255, 0.25); }
              50% { box-shadow: 0 0 14px rgba(108, 99, 255, 0.45); }
            }
          `}</style>
          <div style={{ minHeight: '64px' }} />
          <div key={currentPath} style={{ animation: 'fadeInContent 180ms ease-out' }}>
            {React.cloneElement(props.children, { setLoader: onSetLoader, awayStatus, onSetAwayText })}
          </div>
        </main>
        <ConfirmModal
          message="Are you sure you want to Logout?"
          open={confirmOpen}
          title="Confirm Logout"
          onSubmitConfirm={onConfirmLogout}
          handleClose={handleConfirmClose}
        />
        <CustomAddModal
          open={openAwayText}
          saveLabel="Set Away"
          title="Add Away Text"
          handleClose={() => setOpenAwayText(false)}
          onSubmitConfirm={() => onChangeAwayStatus(true)}
        >
          <Grid container style={{ height: "330px", display: "block" }}>
            <Grid item xs={12} style={{ height: "calc(100% - 60px)" }}>
              <textarea
                className="text-area-input"
                placeholder="Please add a text"
                value={awayText}
                onChange={(e) => setAwayText(e.target.value)}
              />
            </Grid>
          </Grid>
        </CustomAddModal>
      </div>
      <AdminActivityPanel />
      {isSuperAdmin && (
        <LeaveApprovalModal
          open={showLeaveModal}
          onClose={() => setShowLeaveModal(false)}
          setLoader={onSetLoader}
          onActionComplete={() => {
            profileActions.getPendingLeaveRequests()
              .then((res) => setPendingLeaveCount(Array.isArray(res.body) ? res.body.length : 0))
              .catch(() => {});
          }}
        />
      )}
    </>
  );
}
